import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { LocalStorageService } from '@epicuro-next/platform/storage';
import { APP_VERSION, patchSession } from '@epicuro-next/state/session-state';
import { Store } from '@ngrx/store';
import { of, ReplaySubject, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilKeyChanged,
  filter,
  map,
  switchMap,
} from 'rxjs/operators';
export const VERSION_API_URL_TOKEN = new InjectionToken(
  'VERSION_API_URL_TOKEN',
);
export const VERSION_LOCAL_STORAGE_KEY = 'version';
export const VERSION_BRANCH_LOCAL_STORAGE_KEY = 'version-branch';
const salt = () => {
  return String(new Date().getTime() + Math.floor(Math.random() * 1000));
};
export interface AppVersion {
  build: string;
  branch: string;
  commit: string;
}
@Injectable()
export class VersionCheckerService {
  private checkForNewVersion$$ = new Subject<void>();
  public checkForUpdate$$ = new ReplaySubject<void>();
  public prevVersion?: string;
  public currVersion?: string;
  public prevVersionBranch?: string | null;
  public currVersionBranch?: string | null;
  private version$ = this.checkForNewVersion$$.asObservable().pipe(
    switchMap(() =>
      this.httpClient
        .get(`${this.versionUrl}?${salt()}`, {
          headers: {
            'Cache-Control': 'no-store, max-age=0, s-max-age=0',
          },
        })
        .pipe(catchError(() => of(null))),
    ),
    filter(Boolean),
    distinctUntilKeyChanged<AppVersion>('build'),
    map((version: AppVersion | null) => {
      const prevVersion: string | null = this.localStorageService.get(
        VERSION_LOCAL_STORAGE_KEY,
      );
      const prevVersionBranch: string | null = this.localStorageService.get(
        VERSION_BRANCH_LOCAL_STORAGE_KEY,
      );
      return [prevVersion, prevVersionBranch, version];
    }),
  );
  constructor(
    private store: Store,
    private localStorageService: LocalStorageService,
    @Inject(VERSION_API_URL_TOKEN) private versionUrl: string,
    private httpClient: HttpClient,
  ) {
    this.version$.subscribe(
      ([prevVersion, prevVersionBranch, currVersion]: [
        string | null,
        string | null,
        AppVersion | null,
      ]) => {
        if (
          prevVersion &&
          !!prevVersion.length &&
          prevVersion !== currVersion?.build
        ) {
          this.checkForUpdate$$.next();
          this.prevVersion = prevVersion;
          this.currVersion = currVersion?.build;
          this.prevVersionBranch = prevVersionBranch;
          this.currVersionBranch = currVersion?.branch;
        }
        if (currVersion?.build) {
          this.localStorageService.set(
            VERSION_LOCAL_STORAGE_KEY,
            currVersion.build,
          );
        }
        if (currVersion?.branch) {
          this.store.dispatch(
            patchSession({ session: { [APP_VERSION]: currVersion.branch } }),
          );
          this.localStorageService.set(
            VERSION_BRANCH_LOCAL_STORAGE_KEY,
            currVersion.branch,
          );
        }
      },
    );
  }
  public checkForNewVersion() {
    this.checkForNewVersion$$.next();
  }
}
